import React, { useState, useEffect } from "react";
import axios from "axios";
import TopNav from "../../components/navigation/TopNav";
import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import TextInput from "../../components/forms/TextInput";
import StatusMessage from "../../components/ui/StatusMessage";
import { useTranslation } from "react-i18next";

export default function AdminCreateRoute() {
  const { t } = useTranslation();

  const [routeName, setRouteName] = useState("");
  const [startPoint, setStartPoint] = useState("");
  const [endPoint, setEndPoint] = useState("");
  const [notes, setNotes] = useState("");

  const [routes, setRoutes] = useState([]);

  const [message, setMessage] = useState("");
  const [lastAction, setLastAction] = useState("");
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.defaults.headers.common["Authorization"] =
      `Bearer ${localStorage.getItem("token")}`;

    fetchRoutes();
  }, []);

  const getErrorMessage = (err, fallbackKey) => {
    const detail = err?.response?.data?.detail;

    if (typeof detail === "string") return detail;

    if (Array.isArray(detail)) {
      return detail
        .map((item) => {
          const location = Array.isArray(item.loc) ? item.loc.join(".") : "";
          return location ? `${location}: ${item.msg}` : item.msg;
        })
        .filter(Boolean)
        .join(" | ");
    }

    return t(fallbackKey);
  };

  const fetchRoutes = async () => {
    setLoadingRoutes(true);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/routes`
      );

      setRoutes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("❌ Failed to fetch routes", err);
      setRoutes([]);
      setMessage(
        "❌ " + t("admin_create_route.messages.fetch_routes_failed")
      );
      setLastAction("fetch");
    } finally {
      setLoadingRoutes(false);
    }
  };

  const resetForm = () => {
    setRouteName("");
    setStartPoint("");
    setEndPoint("");
    setNotes("");
  };

  const handleSubmit = async () => {
    if (!routeName.trim()) {
      setMessage(
        "❌ " + t("admin_create_route.messages.missing_route_name")
      );
      setLastAction("submit");
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/routes`,
        {
          route_name: routeName.trim(),
          start_point: startPoint.trim() || null,
          end_point: endPoint.trim() || null,
          notes: notes.trim() || null,
        }
      );

      setMessage(
        "✅ " + t("admin_create_route.messages.success")
      );

      setLastAction("submit");

      resetForm();
      fetchRoutes();
    } catch (err) {
      console.error("❌ Error creating route:", err);
      console.error("BACKEND DETAIL:", err?.response?.data);

      setMessage(
        "❌ " +
          getErrorMessage(
            err,
            "admin_create_route.messages.failure"
          )
      );
      setLastAction("submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StandardScreen
      background="default"
      topNav={
        <TopNav
          title={t("admin_create_route.title")}
          mode="main"
        />
      }
    >
      <ScreenContent>
        <div
          style={{
            maxWidth: 720,
            width: "100%",
            margin: "0 auto",
          }}
        >
          <Stack gap="4">
            <Card>
              <Stack gap="4">
                <h1
                  style={{
                    margin: 0,
                    fontSize: "1.5rem",
                    fontWeight: 700,
                  }}
                >
                  {t("admin_create_route.title")}
                </h1>

                <FormField
                  label={t("admin_create_route.fields.route_name.label")}
                >
                  <TextInput
                    value={routeName}
                    onChange={(e) => {
                      setRouteName(e.target.value);
                      setMessage("");
                      setLastAction("");
                    }}
                    placeholder={t(
                      "admin_create_route.fields.route_name.placeholder"
                    )}
                  />
                </FormField>

                <FormField
                  label={t("admin_create_route.fields.start_point.label")}
                >
                  <TextInput
                    value={startPoint}
                    onChange={(e) => {
                      setStartPoint(e.target.value);
                      setMessage("");
                      setLastAction("");
                    }}
                    placeholder={t(
                      "admin_create_route.fields.start_point.placeholder"
                    )}
                  />
                </FormField>

                <FormField
                  label={t("admin_create_route.fields.end_point.label")}
                >
                  <TextInput
                    value={endPoint}
                    onChange={(e) => {
                      setEndPoint(e.target.value);
                      setMessage("");
                      setLastAction("");
                    }}
                    placeholder={t(
                      "admin_create_route.fields.end_point.placeholder"
                    )}
                  />
                </FormField>

                <FormField
                  label={t("admin_create_route.fields.notes.label")}
                >
                  <TextInput
                    value={notes}
                    onChange={(e) => {
                      setNotes(e.target.value);
                      setMessage("");
                      setLastAction("");
                    }}
                    placeholder={t(
                      "admin_create_route.fields.notes.placeholder"
                    )}
                  />
                </FormField>

                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  fullWidth
                >
                  {submitting
                    ? t("admin_create_route.status.submitting")
                    : lastAction === "submit" && message.startsWith("✅")
                    ? t("admin_create_route.status.success_short")
                    : lastAction === "submit" && message.startsWith("❌")
                    ? `${t("admin_create_route.status.error_short")}: ${message.replace("❌ ", "")}`
                    : t("admin_create_route.actions.submit")}
                </Button>
              </Stack>
            </Card>

            <StatusMessage message={message} />

            <Card>
              <Stack gap="3">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "center",
                  }}
                >
                  <h2
                    style={{
                      margin: 0,
                      fontSize: "1.15rem",
                      fontWeight: 700,
                    }}
                  >
                    {t("admin_create_route.list.title")}
                  </h2>

                  <Button
                    onClick={fetchRoutes}
                    disabled={loadingRoutes}
                  >
                    {loadingRoutes
                      ? t("admin_create_route.status.loading")
                      : t("admin_create_route.actions.refresh")}
                  </Button>
                </div>

                {routes.length === 0 ? (
                  <div style={{ opacity: 0.7 }}>
                    {loadingRoutes
                      ? t("admin_create_route.list.loading")
                      : t("admin_create_route.list.empty")}
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                      }}
                    >
                      <thead>
                        <tr>
                          <th style={{ textAlign: "left", padding: "8px 0" }}>
                            {t("admin_create_route.list.columns.route_name")}
                          </th>
                          <th style={{ textAlign: "left", padding: "8px 0" }}>
                            {t("admin_create_route.list.columns.start_point")}
                          </th>
                          <th style={{ textAlign: "left", padding: "8px 0" }}>
                            {t("admin_create_route.list.columns.end_point")}
                          </th>
                          <th style={{ textAlign: "left", padding: "8px 0" }}>
                            {t("admin_create_route.list.columns.status")}
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {routes.map((route) => (
                          <tr key={route.route_id}>
                            <td
                              style={{
                                padding: "8px 0",
                                borderTop: "1px solid rgba(0,0,0,0.08)",
                              }}
                            >
                              {route.route_name || "-"}
                            </td>

                            <td
                              style={{
                                padding: "8px 0",
                                borderTop: "1px solid rgba(0,0,0,0.08)",
                              }}
                            >
                              {route.start_point || "-"}
                            </td>

                            <td
                              style={{
                                padding: "8px 0",
                                borderTop: "1px solid rgba(0,0,0,0.08)",
                              }}
                            >
                              {route.end_point || "-"}
                            </td>

                            <td
                              style={{
                                padding: "8px 0",
                                borderTop: "1px solid rgba(0,0,0,0.08)",
                              }}
                            >
                              {route.status || "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Stack>
            </Card>
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}